import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Button,
  type ButtonProps,
  Divider,
  Grid,
  type GridProps,
  type GridSize,
  IconButton,
  Tooltip,
  Typography,
  type TypographyProps,
} from "@mui/material";
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import useIsMobile from "hooks/useIsMobile";
import Breadcrumbs, { type BreadcrumbsProps } from "../Breadcrumbs";
import LayoutBody from "./LayoutBody";
import LayoutSideBar from "./LayoutSideBar";
import styles from "./layout.module.scss";
export interface LayoutProps {
  /**
   * Pattern and design of the layout to be followed
   */
  mode?: "global" | "default" | "sidebar" | "split";
  /**
   * Content inside the layout
   */
  children?: React.ReactNode;
  headerChildren?: React.ReactNode;

  /**
   * Contents to house inside the layout
   */
  layoutSideBarChildren?: JSX.Element | JSX.Element[];
  /**
   * Header child
   */
  LayoutHeader?: JSX.Element | JSX.Element[];
  headerConfig?: {
    left?: GridSize;
    mid?: GridSize;
    right?: GridSize;
    syncAction?: () => void;
    startComponent?: React.ReactNode;
    endComponent?: React.ReactNode;
    xs?: {
      left?: GridSize;
      mid?: GridSize;
      right?: GridSize;
    };
    md?: {
      left?: GridSize;
      mid?: GridSize;
      right?: GridSize;
    };
    alignItems?: React.CSSProperties["alignItems"];
    justifyContent?: React.CSSProperties["justifyContent"];
  };
  /**
   * Direction of the sidebar
   */
  sideBarDirection?: "left" | "right";
  /**
   * Components to be on the top of the layout
   */
  header?: JSX.Element | JSX.Element[];
  /**
   * TODO
   */
  footer?: JSX.Element | JSX.Element[];
  /**
   * Render primary or secondary buttons
   */
  RenderButtons?: any;
  /**
   * The title of the layout
   */
  title?: string | React.ReactNode;
  /**
   * Primary button props
   */
  primary?: ButtonProps & {
    isCancel?: boolean;
    isLoading?: boolean;
  };
  /**
   * Secondary button props
   */
  secondary?: ButtonProps & {
    isCancel?: boolean;
    isLoading?: boolean;
  };
  /**
   * third button props
   */
  third?: ButtonProps & {
    isCancel?: boolean;
    isLoading?: boolean;
  };

  /**
   * Optional content in the same row as primary/secondary/third buttons (e.g. checkbox).
   */
  buttonRowContent?: React.ReactNode;

  /**
   * If the layout houses viewonly forms only. This is used to determine whether any form validations are to be performed on the layout.
   */

  /**Custom classname */
  className?: string;
  /**Custom sidebar classname */
  sidebarClassName?: string;
  /**Custom body classname */
  bodyClassName?: string;
  /**Custom header component options, `eg:textHeadingOptions:{level:5}` */
  textHeadingOptions?: TypographyProps & { level?: number };
  /**Back button, usually used for mobile screens */
  backArrow?: boolean;
  backLink?: string;
  backAction?: () => void;
  noDivider?: boolean;
  subtitleOptions?: TypographyProps;
  subtitle?: React.ReactNode | string;
  layoutBodyStyles?: React.CSSProperties;
  /** AlignItems argument for the Grid component for the Title, Layout Header and Rendered Button */
  alignItems?: string;
  collapsable?: boolean;
  breadcrumbs?: BreadcrumbsProps;
  sx?: GridProps["sx"];
  layoutGridContainerProps?: GridProps;
  id?: string;
}

const Layout = (props: LayoutProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    header,
    footer,
    mode = "default",
    layoutSideBarChildren,
    sideBarDirection = "left",
    children,
    RenderButtons = null,
    title,
    primary,
    secondary,
    third,
    buttonRowContent,
    className = "",
    sidebarClassName = "",
    bodyClassName = "",
    subtitleOptions,
    subtitle,
    collapsable,
    textHeadingOptions = {
      level: 3,
    },
    LayoutHeader,
    layoutBodyStyles,
    backArrow = isMobile,
    backAction,
    noDivider = false,
    headerConfig = {
      left: 4,
      mid: 4,
      right: 4,
      xs: { left: 4, mid: 4, right: 4 },
      md: { left: 4, mid: 4, right: 4 },
    },
    alignItems = "start",
    backLink = "/dashboard",
    breadcrumbs,
    layoutGridContainerProps,
    id = "layout-container",
    sx,
  } = props;

  const goBack = () => {
    if (backAction) {
      backAction();
    } else {
      navigate(-1);
    }
  };

  const handleGoToLead = () => {
    const currentPath = location.pathname;
    const leadPath = currentPath.replace(/\/accounts\//, '/leads/');
    navigate(leadPath);
  };

  // Check if current path is an account page
  const isAccountPage = location.pathname.includes('/accounts/');

  const RenderButtonsDefault = () => {
    return (
      <Grid
        container
        spacing={1}
        justifyContent="flex-end"
        alignItems="center"
        wrap="nowrap"
      >
        {secondary && (
          <Grid item>
            {(() => {
              const { isCancel, ...rest } = secondary;
              return (
                <LoadingButton
                  color={isCancel ? "error" : "primary"}
                  loading={secondary?.isLoading}
                  onMouseDown={(event: any) => event.preventDefault()}
                  variant="outlined"
                  {...rest}
                />
              );
            })()}
          </Grid>
        )}

        {primary && (
          <Grid item>
            {(() => {
              const { isCancel, ...rest } = primary;
              return (
                <LoadingButton
                  onMouseDown={(event: any) => event.preventDefault()}
                  loading={primary?.isLoading}
                  color={isCancel ? "error" : "primary"}
                  variant="outlined"
                  {...rest}
                />
              );
            })()}
          </Grid>
        )}
        {third && (
          <Grid item>
            {(() => {
              const { isCancel, ...rest } = third;
              return (
                <LoadingButton
                  color={isCancel ? "error" : "primary"}
                  loading={third?.isLoading}
                  onMouseDown={(event: any) => event.preventDefault()}
                  variant="outlined"
                  {...rest}
                />
              );
            })()}
          </Grid>
        )}
      </Grid>
    );
  };

  const Layouts = {
    sidebar: (
      <div className={`${styles["container-sidebar"]} ${className}`} id={id}>
        {header && <header className={styles.header}>{header}</header>}
        <LayoutBody style={layoutBodyStyles}>{children}</LayoutBody>
        <LayoutSideBar
          sideBarDirection={sideBarDirection}
          sidebarClass={sidebarClassName}
          collapsable={collapsable}
        >
          {layoutSideBarChildren as JSX.Element | JSX.Element[]}
        </LayoutSideBar>
        <footer className={styles.footer}>{footer}</footer>
      </div>
    ),
    split: (
      <Grid
        container
        className={`${styles["container-split"]} ${className}`}
        id={id}
      >
        <Grid item xs={12}>
          {header && <header className={styles.header}>{header}</header>}
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={6}>
              <LayoutSideBar
                sideBarDirection={sideBarDirection}
                sidebarClass={sidebarClassName}
              >
                {layoutSideBarChildren as JSX.Element | JSX.Element[]}
              </LayoutSideBar>
            </Grid>
            <Grid item xs={6} className={bodyClassName}>
              <LayoutBody style={layoutBodyStyles}>{children}</LayoutBody>
            </Grid>
          </Grid>
          <footer className={styles.footer}>{footer}</footer>
        </Grid>
      </Grid>
    ),
    global: (
      <Grid
        container
        spacing={2}
        className={`${styles.container} ${className}`}
        id={id}
      >
        <Grid item xs={12}>
          <Grid
            container
            spacing={2}
            className={`${styles.column} ${styles.title}`}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item lg={6} xs={12}>
              <Typography>{title as string}</Typography>
            </Grid>
            <Grid item lg={3} xs={12}>
              {RenderButtons && <RenderButtons />}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          {children}
        </Grid>
      </Grid>
    ),
    default: (
      <Grid
        container
        spacing={0}
        className={`${styles.container} ${className}`}
        id={id}
        sx={{
          p: 0,
          ...sx,
        }}
        {...layoutGridContainerProps}
      >
        <Grid item xs={12}>
          <Grid
            container
            spacing={0}
            alignItems={headerConfig.alignItems ?? alignItems}
            justifyContent={headerConfig.justifyContent ?? "space-between"}
          >
            <Grid
              item
              lg={headerConfig?.left}
              xs={headerConfig.xs?.left ?? headerConfig?.left}
              sx={{ minWidth: 0 }}
            >
              <Grid container spacing={0} wrap="nowrap">
                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Grid
                    container
                    spacing={{ xs: 0 }}
                    alignItems="center"
                    wrap="nowrap"
                  >
                    {backArrow && (
                      <Grid item sx={{ flexShrink: 0 }}>
                        <Tooltip title={`Back to ${backLink}`}>
                          <IconButton onClick={goBack}>
                            <ArrowBackIosIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                    {headerConfig?.startComponent}
                    <Grid
                      item
                      data-testid="layout-title"
                      sx={
                        typeof title !== "string" && title != null
                          ? { minWidth: 0, flex: 1 }
                          : undefined
                      }
                    >
                      {typeof title === "string" || title == null ? (
                        <Typography
                          {...textHeadingOptions}
                          variant={
                            textHeadingOptions?.level
                              ? (`h${textHeadingOptions?.level}` as TypographyProps["variant"])
                              : "h1"
                          }
                        >
                          {title as string}
                        </Typography>
                      ) : (
                        title
                      )}
                    </Grid>

                    {headerConfig?.syncAction && (
                      <Grid item>
                        <IconButton onClick={headerConfig?.syncAction}>
                          <SyncIcon />
                        </IconButton>
                      </Grid>
                    )}
                    {isAccountPage && (
                      <Grid item>
                        <Button variant="contained" onClick={handleGoToLead}>
                          Go To Lead
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {Boolean(LayoutHeader) && (
              <Grid
                item
                lg={headerConfig?.mid}
                xs={headerConfig.xs?.mid ?? headerConfig?.mid}
              >
                {LayoutHeader}
              </Grid>
            )}
            {headerConfig?.endComponent && (
              <Grid
                item
                lg={headerConfig.right || "auto"}
                xs={headerConfig.xs?.right || "auto"}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  minWidth: 0,
                }}
              >
                {headerConfig.endComponent}
              </Grid>
            )}

            {(secondary || primary || third || buttonRowContent) && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {buttonRowContent}
                <RenderButtonsDefault />
              </Grid>
            )}
          </Grid>
        </Grid>
        {subtitle && (
          <Grid item xs={12}>
            {typeof subtitle === "string" ? (
              <Typography {...subtitleOptions}>{subtitle}</Typography>
            ) : (
              subtitle
            )}
          </Grid>
        )}
        {breadcrumbs && (
          <Grid item xs={12}>
            <Breadcrumbs {...breadcrumbs} />
          </Grid>
        )}
        <Grid item xs={12} sx={{ mt: 0.25 }}>
          {noDivider ? <></> : <Divider />}
        </Grid>
        <Grid item xs={12} height={"100%"} sx={{ mt: 0.25 }}>
          {children}
        </Grid>
      </Grid>
    ),
  };
  return Layouts[mode];
};
Layout.displayName = "Layout";
export default Object.assign(Layout, {
  Sidebar: LayoutSideBar,
  Body: LayoutBody,
});
